import { of, switchMap, tap, merge, map, BehaviorSubject, combineLatest, fromEvent } from 'rxjs';

import { replaceViewContainer, renderTemplate } from './ui.js';
import { getDocument, updateDocument, moveDocument, collections$ } from './knowledge-api.js';
import { buildWorkMonitor } from './working.js';

export function createDocumentView(docId) {
    return of(undefined).pipe(
        switchMap(() => {
            let root = renderTemplate('document-view');
            replaceViewContainer(root);

            root.querySelector('.document-title').textContent = `${docId}`;

            let saveMonitor = buildWorkMonitor();
            let saveIndicator$ = saveMonitor.showElements('.saving');
            let alertSavingBeforeUnload$ = saveMonitor.alertBeforeUnload();
            
            let doc$ = getDocument(docId);

            let formLoading$ = new BehaviorSubject(true);

            let formDisabled$ = combineLatest([
                formLoading$,
                saveMonitor.working$,
            ]).pipe(
                map(([formLoading, saving]) => formLoading || saving),
            );

            let form = root.querySelector('form[name=documentForm]');
            let disableForm$ = formDisabled$.pipe(
                tap(disabled => {
                    for (let el of form.querySelectorAll('input, button')) {
                        el.disabled = disabled;
                    }
                }),
            );

            let renderDoc$ = doc$.pipe(
                tap(doc => {
                    form.sourceUri.value = doc.source_uri;
                    form.fileName.value = doc.file_name;
                    let textContainer = root.querySelector('.document-text');
                    textContainer.innerHTML = '';
                    for (let sec of doc.sections) {
                        let pre = document.createElement('pre');
                        pre.textContent = sec;
                        textContainer.appendChild(pre);
                    }
                    formLoading$.next(false);
                }),
            );

            let submit$ = fromEvent(form, 'submit').pipe(
                switchMap(event => {
                    event.preventDefault();
                    return saveMonitor.zone(updateDocument(docId, {
                        source_uri: (form.sourceUri.value || '').trim() || null,
                        file_name: (form.fileName.value || '').trim() || null,
                    }));
                }),
            );

            let moveForm = root.querySelector('form[name=moveForm]');
            let moveFormDisabled$ = saveMonitor.working$.pipe(
                tap(disabled => {
                    for (let el of moveForm.querySelectorAll('select, button')) {
                        el.disabled = disabled;
                    }
                }),
            );

            let renderCollectionOptions$ = collections$.pipe(
                tap(collections => {
                    let select = moveForm.targetCollection;
                    select.innerHTML = '';
                    if (collections.length !== 1) {
                        let opt = document.createElement('option');
                        opt.textContent = '<select>';
                        opt.value = '';
                        select.appendChild(opt);
                    }
                    for (let col of collections) {
                        let opt = document.createElement('option');
                        opt.value = `${col.id}`;
                        opt.textContent = `${col.owner}/${col.name}`;
                        select.appendChild(opt);
                    }
                    if (collections.length === 1) {
                        select.value = `${collections[0].id}`;
                    }
                }),
            );

            let move$ = fromEvent(moveForm, 'submit').pipe(
                switchMap(event => {
                    event.preventDefault();
                    let targetColId = parseInt(moveForm.targetCollection.value);
                    if (!targetColId) {
                        return of(undefined);
                    }
                    return saveMonitor.zone(moveDocument(docId, targetColId));
                }),
            );

            return merge(
                saveIndicator$,
                alertSavingBeforeUnload$,
                disableForm$,
                renderDoc$,
                submit$,
                moveFormDisabled$,
                renderCollectionOptions$,
                move$,
            );
        }),
    );
}
